import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';

// material-ui
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Radio from '@mui/material/Radio';

// icons
import CloseOutlined from '@ant-design/icons/CloseOutlined';
import SwapOutlined from '@ant-design/icons/SwapOutlined';
import CheckCircleFilled from '@ant-design/icons/CheckCircleFilled';
import ArrowRightOutlined from '@ant-design/icons/ArrowRightOutlined';

import { AuthContext } from 'contexts/AuthContext';
import api from 'services/api';
import { getLandingPathForUser, requiresClinicSetup } from 'utils/accessControl';
import toast from 'react-hot-toast';

const getRoleBadgeColor = (roleKey) => {
  switch (roleKey) {
    case 'super_admin':
      return { bg: '#fef2f2', text: '#dc2626', border: '#fecaca' };
    case 'clinic_admin':
      return { bg: '#eff6ff', text: '#2563eb', border: '#bfdbfe' };
    case 'doctor':
      return { bg: '#f0fdf4', text: '#16a34a', border: '#bbf7d0' };
    case 'pharmacist':
      return { bg: '#faf5ff', text: '#9333ea', border: '#e9d5ff' };
    case 'lab_assistant':
      return { bg: '#fff7ed', text: '#ea580c', border: '#fed7aa' };
    default:
      return { bg: '#f8fafc', text: '#475569', border: '#e2e8f0' };
  }
};

export default function RoleSwitchDialog({ open, onClose }) {
  const { user, login } = useContext(AuthContext);
  const [switching, setSwitching] = useState(false);
  const [selectedRoleId, setSelectedRoleId] = useState(null);
  const [fetchedRoles, setFetchedRoles] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(false);

  const availableRoles = (user?.available_roles && user.available_roles.length > 0)
    ? user.available_roles
    : (fetchedRoles.length > 0 ? fetchedRoles : []);

  // Fetch latest roles on dialog open
  React.useEffect(() => {
    if (open) {
      setLoadingRoles(true);
      api.get('/users/my-roles')
        .then((res) => {
          const roles = res.data?.data?.roles || res.data?.roles || [];
          if (roles.length > 0) {
            setFetchedRoles(roles);
          }
        })
        .catch((err) => console.error('Failed to fetch roles:', err))
        .finally(() => setLoadingRoles(false));
    }
  }, [open]);
  
  // Identify currently active role
  const activeRole = availableRoles.find(
    r => Number(r.role_id) === Number(user?.role_id) && 
         String(r.clinic_id || '') === String(user?.clinic_id || '')
  ) || availableRoles[0];

  React.useEffect(() => {
    if (open && activeRole) {
      // Pre-select the currently active role
      setSelectedRoleId(activeRole?.user_role_id || null);
    }
  }, [open, activeRole]);

  const handleSwitch = async () => {
    if (!selectedRoleId || switching) return;
    if (selectedRoleId === activeRole?.user_role_id) {
      onClose();
      return;
    }

    try {
      setSwitching(true);
      const res = await api.post('/users/switch-role', {
        user_role_id: selectedRoleId
      });

      if (res.status >= 200 && res.status < 300) {
        toast.success(res.data?.message || 'Role switched successfully');
        login(res.data.data, true);
        onClose();

        const newUser = res.data?.data?.user || res.data?.data || {};
        if (requiresClinicSetup(newUser)) {
          window.location.href = '/clinic/setup';
        } else {
          window.location.href = getLandingPathForUser(newUser);
        }
      }
    } catch (err) {
      const msg = err?.response?.data?.message || 'Failed to switch role';
      toast.error(msg);
    } finally {
      setSwitching(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={switching ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          p: 0.5,
          boxShadow: '0 12px 36px rgba(0,0,0,0.14)'
        }
      }}
    >
      <DialogTitle sx={{ pb: 1, pt: 2, px: 2.5 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={1}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: 1.5,
                bgcolor: '#e6f0ff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <SwapOutlined style={{ fontSize: 16, color: '#1677ff' }} />
            </Box>
            <Typography variant="h4" sx={{ fontWeight: 800, color: '#0f172a', fontSize: '1.15rem' }}>
              Switch Role / Clinic
            </Typography>
          </Stack>
          <IconButton size="small" onClick={onClose} disabled={switching} sx={{ color: '#94a3b8' }}>
            <CloseOutlined style={{ fontSize: 13 }} />
          </IconButton>
        </Stack>
        <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.8rem', mt: 0.5 }}>
          Select the active profile and clinic you want to work under:
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ px: 2.5, py: 1.5 }}>
        {loadingRoles ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : availableRoles.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 3, color: '#64748b' }}>
            <Typography variant="body2">No available roles found for this account.</Typography>
          </Box>
        ) : (
          <Stack spacing={1.5}>
            {availableRoles.map((r) => {
            const isCurrentActive = activeRole?.user_role_id === r.user_role_id;
            const isSelected = selectedRoleId === r.user_role_id;
            const roleColor = getRoleBadgeColor(r.role_key);

            return (
              <Box
                key={r.user_role_id}
                onClick={() => !switching && setSelectedRoleId(r.user_role_id)}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: '2px solid',
                  borderColor: isSelected ? '#1677ff' : '#e2e8f0',
                  bgcolor: isSelected ? '#f0f7ff' : '#ffffff',
                  cursor: switching ? 'default' : 'pointer',
                  transition: 'all 0.15s ease-in-out',
                  '&:hover': {
                    borderColor: isSelected ? '#1677ff' : '#cbd5e1',
                    bgcolor: isSelected ? '#f0f7ff' : '#f8fafc'
                  }
                }}
              >
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Radio
                      checked={isSelected}
                      onChange={() => setSelectedRoleId(r.user_role_id)}
                      disabled={switching}
                      sx={{ p: 0, color: '#94a3b8', '&.Mui-checked': { color: '#1677ff' } }}
                    />
                    <Box>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography sx={{ fontWeight: 700, fontSize: '0.92rem', color: '#0f172a' }}>
                          {r.role_name}
                        </Typography>
                        {isCurrentActive && (
                          <Chip
                            label="Active Now"
                            size="small"
                            color="success"
                            variant="outlined"
                            sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700 }}
                          />
                        )}
                      </Stack>
                      <Typography sx={{ fontSize: '0.78rem', color: '#64748b', mt: 0.25 }}>
                        {r.clinic_name || 'Independent Profile'}
                        {r.clinic_city ? ` • ${r.clinic_city}` : ''}
                      </Typography>
                    </Box>
                  </Stack>
                  <Chip
                    label={r.role_name}
                    size="small"
                    sx={{
                      fontWeight: 700,
                      fontSize: '0.7rem',
                      bgcolor: roleColor.bg,
                      color: roleColor.text,
                      border: `1px solid ${roleColor.border}`
                    }}
                  />
                </Stack>
              </Box>
            );
          })}
        </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2.5, pb: 2, pt: 1 }}>
        <Button variant="outlined" color="secondary" onClick={onClose} disabled={switching} sx={{ borderRadius: 2 }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={switching || !selectedRoleId || selectedRoleId === activeRole?.user_role_id}
          onClick={handleSwitch}
          endIcon={switching ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <ArrowRightOutlined />}
          sx={{
            borderRadius: 2,
            background: 'linear-gradient(135deg, #1677ff 0%, #0958d9 100%)',
            px: 2.5
          }}
        >
          {switching ? 'Switching...' : 'Switch Role'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

RoleSwitchDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired
};
